import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Megaphone,
  Edit3,
  Trash2,
  Plus,
  Clock,
  User,
  AlertCircle,
  X,
  Upload,
  Save,
  Search,
  Image as ImageIcon,
} from "lucide-react";
import {
  getAllCourseAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "../../../../services/announcement.service";
import {
  card,
  cardInteractive,
  emptyState,
  dividerInner,
  pageTitle,
  cardTitle,
  microLabel,
  bodySecondary,
  accentBarSection,
  accentBarCard,
  badgeBase,
  iconSize,
  surfaceMuted,
  motionFadeUp,
  motionStaggerChild,
  motionPageTransition,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import { Skeleton, SkeletonCard } from "../../../StudentDashboard/Components/ui/Skeleton";
import toast from "react-hot-toast";

// ---------------------------------------------------------------------------
// SKELETON
// ---------------------------------------------------------------------------

const AnnouncementSkeleton = () => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div>
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-1 w-12 mt-3" />
      <Skeleton className="h-4 w-72 mt-3" />
    </div>
    <Skeleton className="h-10 w-56" />
    {/* Cards skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {[...Array(6)].map((_, i) => (
        <SkeletonCard key={i} className="overflow-hidden">
          <Skeleton className="h-44 w-full rounded-none" />
          <div className="p-4 space-y-3">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-4/5" />
          </div>
        </SkeletonCard>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// BUTTON HELPERS
// ---------------------------------------------------------------------------

const btnBase =
  "flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-bold transition-colors";

const btnStyles = {
  primary: cn(btnBase, "bg-accent1 text-white hover:bg-accent1/90"),
  muted: cn(
    btnBase,
    "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
  ),
  danger: cn(btnBase, "bg-red-600 text-white hover:bg-red-700"),
  ghost: cn(
    btnBase,
    "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
  ),
};

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

const AnnouncementManagement = ({ courseID }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    image: null,
  });
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch announcements
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await getAllCourseAnnouncements({ courseID });
      setAnnouncements(response.announcements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData((prev) => ({ ...prev, image: file }));
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = new FormData();
    form.append("title", formData.title);
    form.append("content", formData.content);
    if (formData.image) form.append("image", formData.image);

    try {
      if (editingAnnouncement) {
        await updateAnnouncement(courseID, editingAnnouncement._id, form);
        setEditingAnnouncement(null);
        toast.success("Announcement updated!");
      } else {
        await createAnnouncement(courseID, form);
        toast.success("Announcement published!");
      }
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error("Error saving announcement:", error);
      toast.error("Failed to save announcement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (announcementID) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      try {
        await deleteAnnouncement(courseID, announcementID);
        fetchAnnouncements();
      } catch (error) {
        console.error("Error deleting announcement:", error);
      }
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      image: null,
    });
    setImagePreview(announcement.image?.imageUrl || null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setFormData({ title: "", content: "", image: null });
    setEditingAnnouncement(null);
    setImagePreview(null);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const timeAgo = (dateString) => {
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    const intervals = [
      { label: "y", seconds: 31536000 },
      { label: "mo", seconds: 2592000 },
      { label: "d", seconds: 86400 },
      { label: "h", seconds: 3600 },
      { label: "m", seconds: 60 },
    ];
    for (const { label, seconds: s } of intervals) {
      const count = Math.floor(seconds / s);
      if (count >= 1) return `${count}${label} ago`;
    }
    return "Just now";
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [courseID]);

  // Filter announcements
  const filteredAnnouncements = announcements.filter((a) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      a.title?.toLowerCase().includes(s) ||
      a.content?.toLowerCase().includes(s)
    );
  });

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <motion.div {...motionFadeUp} className="space-y-6 p-4 sm:p-6">
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={pageTitle}>Course Announcements</h1>
          <div className={cn(accentBarSection, "mt-3")} />
          <p className={cn(bodySecondary, "mt-3")}>
            Manage communications with your students
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className={btnStyles.primary}
          >
            <Plus className={iconSize.md} />
            New Announcement
          </button>
        )}
      </div>

      {/* ── Create / Edit Form ──────────────────────────────────── */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            key="form"
            {...motionPageTransition}
            className={cn(card, "overflow-hidden")}
          >
            {/* Form header */}
            <div
              className={cn(
                "px-6 py-4 flex items-center justify-between border-b",
                dividerInner
              )}
            >
              <div className="flex items-center gap-3">
                <Megaphone className={cn(iconSize.lg, "text-accent1")} />
                <h2 className={cardTitle}>
                  {editingAnnouncement
                    ? "Edit Announcement"
                    : "Create New Announcement"}
                </h2>
              </div>
              <button
                onClick={resetForm}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
              >
                <X className={iconSize.lg} />
              </button>
            </div>

            {/* Form body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Title */}
              <div>
                <label className={cn(microLabel, "mb-2 block")}>
                  Announcement Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full p-3 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors",
                    dividerInner
                  )}
                  placeholder="Enter a descriptive title"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className={cn(microLabel, "mb-2 block")}>
                  Announcement Content
                </label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full min-h-[180px] p-4 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors",
                    dividerInner
                  )}
                  placeholder="Enter the announcement details here..."
                  rows="6"
                  required
                />
              </div>

              {/* Image upload */}
              <div>
                <label className={cn(microLabel, "mb-2 block")}>
                  Announcement Image (Optional)
                </label>
                <label
                  className={cn(
                    "flex flex-col items-center justify-center w-full h-56 border-2 border-dashed rounded-sm cursor-pointer transition-colors",
                    imagePreview
                      ? "border-accent1/40 bg-accent1/5"
                      : "border-gray-300 dark:border-gray-600 hover:border-accent1/40 hover:bg-accent1/5 dark:hover:bg-accent1/10"
                  )}
                >
                  {imagePreview ? (
                    <div className="relative flex flex-col items-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="max-h-40 max-w-full rounded-sm object-contain"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setImagePreview(null);
                          setFormData((prev) => ({ ...prev, image: null }));
                        }}
                        className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 font-bold transition-colors"
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 mb-2 text-gray-400 dark:text-gray-500" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-bold text-accent1">
                          Click to upload
                        </span>{" "}
                        or drag and drop
                      </p>
                      <p className={cn(bodySecondary, "mt-1")}>
                        PNG, JPG or GIF (MAX. 2MB)
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                </label>
              </div>

              {/* Form actions */}
              <div
                className={cn(
                  "flex justify-end gap-3 pt-4 border-t",
                  dividerInner
                )}
              >
                <button
                  type="button"
                  onClick={resetForm}
                  className={btnStyles.muted}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={cn(
                    btnStyles.primary,
                    "min-w-[180px] justify-center",
                    isSubmitting && "opacity-70 cursor-not-allowed"
                  )}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className={iconSize.md} />
                      {editingAnnouncement
                        ? "Update Announcement"
                        : "Publish Announcement"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Announcements List ──────────────────────────────────── */}
      <div className={cn(card, "overflow-hidden")}>
        {/* List header */}
        <div
          className={cn(
            "px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b",
            dividerInner
          )}
        >
          <div className="flex items-center gap-3">
            <h2 className={cardTitle}>All Announcements</h2>
            <span
              className={cn(
                badgeBase,
                "bg-accent1/10 text-accent1 border-accent1/30"
              )}
            >
              {announcements.length}
            </span>
          </div>
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search
              className={cn(
                iconSize.md,
                "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              )}
            />
            <input
              type="text"
              placeholder="Search announcements..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={cn(
                "w-full pl-9 pr-4 py-2 border rounded-sm focus:ring-2 focus:ring-accent1/30 focus:border-accent1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 text-sm",
                dividerInner
              )}
            />
          </div>
        </div>

        {/* List body */}
        <div className="p-6">
          {loading ? (
            <AnnouncementSkeleton />
          ) : filteredAnnouncements.length === 0 ? (
            <div className={emptyState}>
              <Megaphone
                className={cn(
                  iconSize.empty,
                  "mx-auto text-gray-400 dark:text-gray-500 mb-3"
                )}
              />
              <p className="font-bold text-gray-700 dark:text-gray-300 mb-1">
                {announcements.length === 0
                  ? "No Announcements Yet"
                  : "No Matching Announcements"}
              </p>
              <p className={bodySecondary}>
                {announcements.length === 0
                  ? "Create your first announcement to communicate with your students."
                  : "Try adjusting your search term."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredAnnouncements.map((announcement, i) => (
                <motion.div
                  key={announcement._id}
                  {...motionStaggerChild(i)}
                  whileHover={{ y: -2 }}
                  className={cn(
                    cardInteractive,
                    "flex flex-col"
                  )}
                >
                  {/* Image */}
                  <div className="relative h-44 bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <img
                      src={
                        announcement.image?.imageUrl || "/announcement.png"
                      }
                      alt={announcement.title}
                      className="w-full h-full object-cover"
                    />
                    {/* Time ago badge */}
                    <span
                      className={cn(
                        "absolute top-3 right-3 px-2 py-1 rounded-sm text-xs font-bold bg-black/60 text-white backdrop-blur-sm"
                      )}
                    >
                      {timeAgo(announcement.publishDate)}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4">
                    <h3 className={cn(cardTitle, "truncate")}>
                      {announcement.title}
                    </h3>
                    <div
                      className={cn(
                        bodySecondary,
                        "mt-1.5 flex items-center gap-1.5"
                      )}
                    >
                      <Clock className={iconSize.xs} />
                      <span>{formatDate(announcement.publishDate)}</span>
                    </div>
                    {announcement.publishedBy?.user?.name && (
                      <div
                        className={cn(
                          bodySecondary,
                          "mt-1 flex items-center gap-1.5"
                        )}
                      >
                        <User className={iconSize.xs} />
                        <span>{announcement.publishedBy.user.name}</span>
                      </div>
                    )}
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 line-clamp-3 leading-relaxed">
                      {announcement.content}
                    </p>
                  </div>

                  {/* Actions */}
                  <div
                    className={cn(
                      "flex items-center justify-end gap-1 px-4 py-3 border-t",
                      dividerInner
                    )}
                  >
                    <button
                      onClick={() => handleEdit(announcement)}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-accent1 hover:bg-accent1/10 rounded-sm transition-colors"
                      title="Edit"
                    >
                      <Edit3 className={iconSize.md} />
                    </button>
                    <button
                      onClick={() => handleDelete(announcement._id)}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm transition-colors"
                      title="Delete"
                    >
                      <Trash2 className={iconSize.md} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Info Banner ──────────────────────────────────────────── */}
      <div
        className={cn(
          "flex items-start gap-3 p-4 rounded-sm border bg-accent1/5 border-accent1/20 dark:bg-accent1/10 dark:border-accent1/30"
        )}
      >
        <AlertCircle
          className={cn(iconSize.lg, "text-accent1 mt-0.5 flex-shrink-0")}
        />
        <div>
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-0.5">
            About Announcements
          </p>
          <p className={bodySecondary}>
            Announcements are visible to all students enrolled in your course.
            Students will receive a notification when a new announcement is
            published.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AnnouncementManagement;
